export interface CapturedPcmFrame {
  audio: string;
  rms: number;
}

export function downsampleToPcm16(
  input: Float32Array,
  inputSampleRate: number,
  outputSampleRate = 16000,
): Int16Array {
  if (outputSampleRate > inputSampleRate) {
    throw new Error(`Cannot upsample microphone audio from ${inputSampleRate}Hz to ${outputSampleRate}Hz`);
  }
  const ratio = inputSampleRate / outputSampleRate;
  const outputLength = Math.floor(input.length / ratio);
  const output = new Int16Array(outputLength);
  for (let outputIndex = 0; outputIndex < outputLength; outputIndex += 1) {
    const start = Math.floor(outputIndex * ratio);
    const end = Math.min(input.length, Math.floor((outputIndex + 1) * ratio));
    let sum = 0;
    for (let inputIndex = start; inputIndex < end; inputIndex += 1) {
      sum += input[inputIndex];
    }
    const sample = Math.max(-1, Math.min(1, sum / Math.max(1, end - start)));
    output[outputIndex] = sample < 0 ? sample * 32768 : sample * 32767;
  }
  return output;
}

export function pcm16ToBase64(samples: Int16Array): string {
  const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
  let binary = '';
  const blockSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += blockSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + blockSize));
  }
  return btoa(binary);
}

export function base64ToPcm16(value: string): Float32Array {
  const binary = atob(value);
  if (binary.length % 2 !== 0) {
    throw new Error('Realtime audio frame is not 16-bit PCM');
  }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const view = new DataView(bytes.buffer);
  const samples = new Float32Array(bytes.length / 2);
  for (let index = 0; index < samples.length; index += 1) {
    samples[index] = view.getInt16(index * 2, true) / 32768;
  }
  return samples;
}

function rmsFor(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let squares = 0;
  for (let index = 0; index < samples.length; index += 1) {
    squares += samples[index] * samples[index];
  }
  return Math.sqrt(squares / samples.length);
}

export class RealtimeMicCapture {
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private silentSink: GainNode | null = null;

  async start(onFrame: (frame: CapturedPcmFrame) => void): Promise<void> {
    if (this.stream) return;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
    this.context = new AudioContext();
    await this.context.resume();
    this.source = this.context.createMediaStreamSource(this.stream);
    this.processor = this.context.createScriptProcessor(4096, 1, 1);
    this.processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      const pcm = downsampleToPcm16(input, this.context?.sampleRate || 48000);
      onFrame({ audio: pcm16ToBase64(pcm), rms: rmsFor(input) });
    };
    this.source.connect(this.processor);
    // ScriptProcessorNode needs an output connection to keep processing, but
    // routing it to the speakers would create microphone feedback.
    this.silentSink = this.context.createGain();
    this.silentSink.gain.value = 0;
    this.processor.connect(this.silentSink);
    this.silentSink.connect(this.context.destination);
  }

  async stop(): Promise<void> {
    if (this.processor) {
      this.processor.onaudioprocess = null;
      this.processor.disconnect();
    }
    this.silentSink?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    if (this.context && this.context.state !== 'closed') {
      await this.context.close();
    }
    this.processor = null;
    this.silentSink = null;
    this.source = null;
    this.stream = null;
    this.context = null;
  }
}

export class RealtimePcmPlayer {
  private readonly context = new AudioContext();
  private readonly sources = new Set<AudioBufferSourceNode>();
  private nextStartTime = 0;

  async resume(): Promise<void> {
    await this.context.resume();
  }

  enqueue(base64Audio: string, sampleRate = 24000): void {
    const samples = base64ToPcm16(base64Audio);
    const buffer = this.context.createBuffer(1, samples.length, sampleRate);
    buffer.getChannelData(0).set(samples);
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.context.destination);
    this.sources.add(source);
    source.onended = () => this.sources.delete(source);
    const startAt = Math.max(this.context.currentTime + 0.02, this.nextStartTime);
    source.start(startAt);
    this.nextStartTime = startAt + buffer.duration;
  }

  async stop(): Promise<void> {
    this.sources.forEach((source) => {
      try {
        source.stop();
      } catch {
        // Source may have ended between iteration and stop.
      }
    });
    this.sources.clear();
    if (this.context.state !== 'closed') {
      await this.context.close();
    }
  }
}
